import {
	CheckboxVisibility,
	DefaultButtonCompat as DefaultButton,
	DetailsListCompat as DetailsList,
	DetailsListLayoutMode,
	DetailsRowCompat as DetailsRow,
	DialogCompat as Dialog,
	DialogFooterCompat as DialogFooter,
	DialogType,
	DropdownCompat as Dropdown,
	IDetailsHeaderProps,
	IDetailsRowBaseProps,
	IDetailsRowProps,
	IDropdownOption,
	IRenderFunction,
	PrimaryButtonCompat as PrimaryButton,
	ScrollablePaneCompat as ScrollablePane,
	StackCompat as Stack,
	TextCompat as Text,
	ToggleCompat as Toggle,
	TooltipHostCompat as TooltipHost,
	useTheme,
} from 'components/FluentUICompat';
import {useBoolean} from '@fluentui/react-hooks';
import {useSelection} from 'hooks';
import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {
	compareParagraphs,
	CompareResult,
	IComparedItem,
} from '../../compareDocuments/compareDocuments';
import {CompareVersionPanelTooltipTranslationProvider} from '../CompareVersionPanelTooltipTranslationProvider';
import {
	CheckboxWithTooltip,
	ColumnWithTooltipOptions,
	ProviderThatEnablesGettingTooltipsFromContext,
	getRendererForHeaderWithTooltips,
} from 'features/localizedTooltips';
import {ParagraphItem} from './ParagraphItem';
import {useTranslationsOfCompareVersionPanel} from '../CompareVersionPanel.utils';
import {ResultTypeFilterOption} from './ResultTypeFilterOption';
import {useGetAddRequirementsFormDataQuery} from 'features/RegulatoryDocuments/hooks/useGetAddRequirementsFormData.generated';
import {useUserContext} from 'authentication/UserContext';
import {useGetCompareDocumentDataLazyQuery} from 'features/RegulatoryDocuments/hooks';
import {useGetRegulationQuery} from 'features/Regulations/hooks';
import {RegulatoryDocument, WorkflowStatus} from 'types';
import {LoadWrapper} from 'components/LoadWrapper';
import {CalloutLoader} from 'components/CalloutLoader';
import {useTranslation} from 'react-i18next';
import {
	ComparisonUpdateFields,
	ComparisonUpdateFieldType,
} from '../compareVersionTypes';
import {useComparisonUpdate} from '../_useComparisonUpdate';
import {useComparisonFilter} from '../_useComparisonFilters';

const comparisonListStyles = {
	root: {
		overflowX: 'hidden',
	},
};

const onRenderDetailsHeader: IRenderFunction<IDetailsHeaderProps> =
	getRendererForHeaderWithTooltips(true);

export type ComparisonComponentOnTransferredType = (
	regDocId: string,
	items: IComparedItem[],
	selectedFields: ComparisonUpdateFields,
) => void;

export interface ComparisonComponentProps {
	regulationId: string;
	onTransferred: ComparisonComponentOnTransferredType;
}

export const ComparisonComponent: React.FC<ComparisonComponentProps> = ({
	regulationId,
	onTransferred,
}) => {
	const {t} = useTranslationsOfCompareVersionPanel();
	const {t: tEnum} = useTranslation('common/enums', {
		keyPrefix: 'WorkflowStatus',
	});
	const theme = useTheme();

	const {isVko, isAvk, isBav, isAdmin, isVex, isRegulationReader, isReader} =
		useUserContext();
	/**
	 * Queries
	 */
	const [
		loadOldParagraphData,
		{data: oldVersionData, loading: areOldParagraphsLoading},
	] = useGetCompareDocumentDataLazyQuery();

	const [
		loadNewParagraphData,
		{data: newVersionData, loading: areNewParagraphsLoading},
	] = useGetCompareDocumentDataLazyQuery();

	const {data: dataRegulation, loading: loadingRegulation} =
		useGetRegulationQuery({variables: {regulationId}});

	const {data, loading} = useGetAddRequirementsFormDataQuery({
		skip: isAvk || isBav,
	});

	const oldVersion = oldVersionData?.regulatoryDocument;
	const newVersion = newVersionData?.regulatoryDocument;

	const [selection, selectedItems] = useSelection<IComparedItem>();
	const {
		appendMetadata,
		replaceMetadata,
		checkboxData,
		selectField,
		unselectField,
		selectAll,
		deselectAll,
		selectedFields,
		isLoading: transferInProgress,
	} = useComparisonUpdate(
		selectedItems,
		oldVersion ?? ({} as RegulatoryDocument),
		newVersion ?? ({} as RegulatoryDocument),
		data,
	);
	const {filterByStatus, filterVexAssigned} = useComparisonFilter();
	const [compareResult, setCompareResult] = useState<IComparedItem[]>([]); //comparison result before update
	const [oldVersionIntern, setOldVersionIntern] = useState<IDropdownOption>();
	const [newVersionIntern, setNewVersionIntern] = useState<IDropdownOption>();
	const [filteredCompareResult, setFilteredCompareResult] = useState<
		IComparedItem[]
	>([]); //modified after update
	const [showDeleted, {toggle: toggleDeleted}] = useBoolean(false);
	const [showNew, {toggle: toggleNew}] = useBoolean(false);
	const [showChanged, {toggle: toggleChanged}] = useBoolean(false);
	const [showEqual, {toggle: toggleEqual}] = useBoolean(false);
	const [showInReview, {toggle: toggleInReview}] = useBoolean(false);
	const [showMetadata, {toggle: toggleMetadata}] = useBoolean(true);
	const [showVexAssigned, {toggle: toggleVexAssigned}] = useBoolean(isVex);
	const [hideDialog, {toggle: toggleHideDialog}] = useBoolean(true);
	const [confirmDialogStatus, setConfirmDialogStatus] = useState<
		'appendMetadata' | 'replaceMetadata' | 'hidden'
	>('hidden');
	const [selectAllChecked, setSelectAllChecked] = useState(true);

	useEffect(() => {
		if (oldVersion && newVersion) {
			setCompareResult(
				compareParagraphs(oldVersion.paragraphs, newVersion.paragraphs),
			);
		}
		return () => setCompareResult([]);
	}, [oldVersion, newVersion]);

	const onAppendMetadataClick = useCallback(async () => {
		const newComparisonItems = await appendMetadata([...filteredCompareResult]);
		setFilteredCompareResult(newComparisonItems);
		if (newVersion?.id) {
			onTransferred(newVersion.id, newComparisonItems, selectedFields);
		}
	}, [appendMetadata, filteredCompareResult, newVersion?.id, onTransferred]);

	const onReplaceMetadataClick = useCallback(async () => {
		const newComparisonItems = await replaceMetadata([
			...filteredCompareResult,
		]);
		setFilteredCompareResult(newComparisonItems);
		if (newVersion?.id) {
			onTransferred(newVersion.id, newComparisonItems, selectedFields);
		}
	}, [filteredCompareResult, newVersion?.id, onTransferred, replaceMetadata]);

	const ConfirmMetadataDialog = () => {
		const onConfirm = () => {
			switch (confirmDialogStatus) {
				case 'appendMetadata':
					onAppendMetadataClick();
					break;
				case 'replaceMetadata':
					onReplaceMetadataClick();
					break;
				case 'hidden':
				default:
					break;
			}
			if (!hideDialog) {
				toggleHideDialog();
			}
			setConfirmDialogStatus('hidden');
		};

		return (
			<Dialog
				hidden={confirmDialogStatus === 'hidden'}
				dialogContentProps={{
					type: DialogType.normal,
					title:
						confirmDialogStatus === 'appendMetadata'
							? t('ConfirmAppendMetadataDialogTitle')
							: t('ConfirmCopyReplaceMetadataDialogTitle'),
					closeButtonAriaLabel: 'Close',
				}}
			>
				<Text>
					{confirmDialogStatus === 'appendMetadata'
						? t('ConfirmAppendMetadataDialogMessage')
						: t('ConfirmCopyReplaceMetadataDialogMessage')}
				</Text>
				<br />
				<Text>{t('SelectMetadataDialogMessage')}:</Text>
				<br />
				{checkboxData.map(cb => {
					return cb.isSelected ? (
						<>
							<Text styles={{root: {fontWeight: 'bold'}}}>{cb.value}</Text>
							<br />
						</>
					) : (
						<></>
					);
				})}
				<Text>
					{confirmDialogStatus === 'appendMetadata'
						? t('ConfirmAppendMetadataDialogMessageQuestion')
						: t('ConfirmCopyReplaceMetadataDialogMessageQuestion')}
				</Text>
				<DialogFooter>
					<PrimaryButton onClick={onConfirm} text={t('Confirm')} />
					<DefaultButton
						onClick={() => setConfirmDialogStatus('hidden')}
						text={t('Cancel')}
					/>
				</DialogFooter>
			</Dialog>
		);
	};

	useEffect(() => {
		const statusFilteredItems = filterByStatus(compareResult, {
			showDeleted,
			showNew,
			showChanged,
			showEqual,
			showInReview,
		});
		const vexAssignedItems = filterVexAssigned(
			statusFilteredItems,
			showVexAssigned,
		);
		setFilteredCompareResult(vexAssignedItems);
	}, [
		compareResult,
		showDeleted,
		showNew,
		showChanged,
		showEqual,
		showInReview,
		showVexAssigned,
	]);

	const SelectMetadataDialog = () => (
		<Dialog
			hidden={hideDialog || confirmDialogStatus !== 'hidden'}
			onDismiss={toggleHideDialog}
			dialogContentProps={{
				type: DialogType.normal,
				title: t('SelectMetadataDialogTitle'),
				closeButtonAriaLabel: 'Close',
			}}
			modalProps={{
				styles: {main: {maxWidth: '600px !important'}},
			}}
		>
			<Text>{t('SelectMetadataDialogSubtext')}:</Text>
			<br />
			<Text styles={{root: {fontWeight: 'bold'}}}>{t('AppendMetadata')}</Text>
			<Text> - {t('AppendMetadataDescription')}</Text>
			<br />
			<Text styles={{root: {fontWeight: 'bold'}}}>{t('ReplaceMetadata')}</Text>
			<Text> - {t('ReplaceMetadataDescription')}</Text>
			<br />
			<br />
			<Text>{t('SelectMetadataDialogMessage')}:</Text>
			<CheckboxWithTooltip
				styles={{root: {paddingLeft: 10, paddingTop: 2, paddingBottom: 8}}}
				key={'selectAll'}
				name={'selectAll'}
				label={t('SelectAll')}
				checked={selectAllChecked}
				onChange={item => {
					const isChecked = (item?.target as any).checked === true;
					if (isChecked) {
						selectAll();
					} else {
						deselectAll();
					}
					setSelectAllChecked(isChecked);
				}}
			></CheckboxWithTooltip>
			{checkboxData.map(fieldItem => (
				<CheckboxWithTooltip
					styles={{root: {paddingLeft: 10, paddingTop: 2}}}
					key={fieldItem.key}
					name={fieldItem.key}
					label={fieldItem.value}
					checked={fieldItem.isSelected}
					onChange={item => {
						const field = (item?.target as any)
							.name as ComparisonUpdateFieldType;
						const isChecked = (item?.target as any).checked === true;
						if (isChecked) {
							selectField(field);
						} else {
							unselectField(field);
						}
					}}
				></CheckboxWithTooltip>
			))}
			<DialogFooter styles={{actions: {textAlign: 'center'}}}>
				<PrimaryButton
					onClick={() => setConfirmDialogStatus('appendMetadata')}
					text={t('AppendMetadata')}
				/>
				<PrimaryButton
					onClick={() => setConfirmDialogStatus('replaceMetadata')}
					text={t('ReplaceMetadata')}
				/>
				<DefaultButton onClick={toggleHideDialog} text={t('Cancel')} />
			</DialogFooter>
		</Dialog>
	);

	const columns: ColumnWithTooltipOptions[] = [
		{
			key: 'oldVersion',
			name: oldVersion?.name ?? '',
			minWidth: 100,
			flexGrow: 1,
			isResizable: true,
			tooltipHostProps: {translationKey: 'oldVersion'},
			onRender: (item: IComparedItem) => (
				<ParagraphItem
					comparedParagraph={item.oldParagraph}
					resultType={item.resultType}
					showComparisonMark={true}
					showMetadata={showMetadata}
					theme={theme}
				/>
			),
		},
		{
			key: 'newVersion',
			name: newVersion?.name ?? '',
			minWidth: 100,
			flexGrow: 1,
			isResizable: true,
			tooltipHostProps: {translationKey: 'newVersion'},
			onRender: (item: IComparedItem) => (
				<ParagraphItem
					comparedParagraph={item.newParagraph}
					resultType={item.resultType}
					showComparisonMark={false}
					showMetadata={showMetadata}
					theme={theme}
				/>
			),
		},
	];

	const optionsOld: IDropdownOption[] = useMemo(() => {
		const regDocs = dataRegulation?.regulation?.regulatoryDocuments ?? [];
		if (isVko || isAdmin || isAvk) {
			return regDocs.map(x => ({
				key: x.id,
				text: `${x.name} (Workflow: ${tEnum(x.workflow.status)})`,
			}));
		}
		return regDocs
			.filter(x => x.workflow.status === WorkflowStatus.Finalized)
			.map(x => ({key: x.id, text: x.name}));
	}, [dataRegulation, isVko, isAvk, isAdmin, isVex]);

	const optionsNew: IDropdownOption[] = useMemo(() => {
		const regDocs = dataRegulation?.regulation?.regulatoryDocuments ?? [];
		if (isVko || isAdmin || isAvk) {
			return regDocs
				.filter(
					x =>
						![
							WorkflowStatus.New,
							WorkflowStatus.QualityControlExternal,
						].includes(x.workflow.status),
				)
				.map(x => ({
					key: x.id,
					text: `${x.name} (Workflow: ${tEnum(x.workflow.status)})`,
				}));
		}
		return regDocs
			.filter(x => x.workflow.status === WorkflowStatus.Finalized)
			.map(x => ({key: x.id, text: x.name}));
	}, [dataRegulation, isVko, isAvk, isAdmin, isVex]);

	const disableTransferRoleBased = useMemo(() => {
		if (isAdmin || isVex) {
			return false;
		}
		if (isVko) {
			const regDocs = dataRegulation?.regulation?.regulatoryDocuments ?? [];
			return ![
				WorkflowStatus.InProgressInternal,
				WorkflowStatus.Examination,
				WorkflowStatus.Modified,
			].includes(
				regDocs.find(x => x.id === newVersion?.id)?.workflow.status ??
					WorkflowStatus.Finalized,
			);
		}
		return true;
	}, [dataRegulation, newVersion, isVko, isAdmin, isVex]);

	const onChangeOld = (
		event: React.FormEvent<HTMLDivElement>,
		item?: IDropdownOption,
	): void => {
		setOldVersionIntern(item);
		if (item) {
			loadOldParagraphData({
				variables: {
					regulatoryDocumentId: String(item.key),
				},
			});
		}
	};

	const onChangeNew = (
		event: React.FormEvent<HTMLDivElement>,
		item?: IDropdownOption,
	): void => {
		setNewVersionIntern(item);
		if (item) {
			loadNewParagraphData({
				variables: {
					regulatoryDocumentId: String(item.key),
				},
			});
		}
	};

	const onRenderRow = (props?: IDetailsRowProps) => {
		// eslint-disable-next-line react/prop-types
		if (isVex && props && props.item?.newParagraph === undefined) {
			props.styles = {
				root: {
					background: `${theme.palette.neutralDark}33`,
					opacity: '0.5 !important',
				},
			};
		}
		return <DetailsRow {...(props as IDetailsRowProps)} />;
	};

	return (
		<Stack>
			<ConfirmMetadataDialog />
			<SelectMetadataDialog />
			<CalloutLoader isLoading={transferInProgress} />
			<Stack horizontal horizontalAlign='space-between' padding={'0 50px 0 0 '}>
				<Stack horizontal>
					<ResultTypeFilterOption
						resultType={CompareResult.Deleted}
						labelTranslationKey='Deleted'
						onChangeCheckbox={toggleDeleted}
					/>
					<ResultTypeFilterOption
						resultType={CompareResult.New}
						labelTranslationKey='Added'
						onChangeCheckbox={toggleNew}
					/>
					<ResultTypeFilterOption
						resultType={CompareResult.Changed}
						labelTranslationKey='TextChange'
						onChangeCheckbox={toggleChanged}
					/>
					<ResultTypeFilterOption
						resultType={CompareResult.Equal}
						labelTranslationKey='NoChange'
						onChangeCheckbox={toggleEqual}
					/>
					<ResultTypeFilterOption
						resultType={CompareResult.InReview}
						labelTranslationKey='InReview'
						onChangeCheckbox={toggleInReview}
					/>
				</Stack>

				<Stack horizontal tokens={{childrenGap: 32}}>
					{isVex && (
						<Toggle
							label={t('ShowVexAssigned')}
							inlineLabel
							checked={showVexAssigned}
							onChange={toggleVexAssigned}
						/>
					)}
					<Toggle
						label={t('ShowMetadata')}
						inlineLabel
						checked={showMetadata}
						onChange={toggleMetadata}
					/>
					{!(isRegulationReader || isReader || isAvk) && (
						<TooltipHost
							content={loading || transferInProgress ? t('DataLoading') : ''}
						>
							<PrimaryButton
								text={t('CopyMetadata')}
								onClick={toggleHideDialog}
								styles={{root: {marginRight: 15}}}
								disabled={
									selectedItems.length === 0 ||
									loading ||
									transferInProgress ||
									disableTransferRoleBased
								}
							/>
						</TooltipHost>
					)}
				</Stack>
			</Stack>
			<Stack horizontal style={{padding: 10}} horizontalAlign='space-evenly'>
				<Dropdown
					label={t('OldVersion')}
					options={optionsOld}
					style={{width: '40vw'}}
					onChange={onChangeOld}
					selectedKey={oldVersionIntern ? oldVersionIntern.key : undefined}
				/>

				<Dropdown
					label={t('NewVersion')}
					options={optionsNew}
					style={{width: '40vw'}}
					onChange={onChangeNew}
					selectedKey={newVersionIntern ? newVersionIntern.key : undefined}
				/>
			</Stack>
			<div style={{position: 'relative', height: 'calc(100vh - 190px)'}}>
				{oldVersion && newVersion && (
					<ScrollablePane>
						<CompareVersionPanelTooltipTranslationProvider>
							<ProviderThatEnablesGettingTooltipsFromContext>
								<DetailsList
									items={filteredCompareResult}
									layoutMode={DetailsListLayoutMode.fixedColumns}
									checkboxVisibility={CheckboxVisibility.always}
									styles={comparisonListStyles}
									selection={selection}
									onRenderDetailsHeader={onRenderDetailsHeader}
									columns={columns}
									onRenderRow={onRenderRow}
								/>
							</ProviderThatEnablesGettingTooltipsFromContext>
						</CompareVersionPanelTooltipTranslationProvider>
					</ScrollablePane>
				)}
				{(areOldParagraphsLoading || areNewParagraphsLoading) && (
					<LoadWrapper loading={true} />
				)}
			</div>
		</Stack>
	);
};
